import { useRef, useState } from 'react';
import { track, type Events } from './track';

// `source` tags every funnel event so the per-surface funnels stay separable
// in PostHog. `properties` are extra Mailjet contact properties a surface
// collects (the social textarea becomes social_data server-side); the server
// drops blanks and unknown names.
export function useWaitlistSignup(
  source: Events['landing_signup_submitted']['source'],
  properties?: { country: string; feedback?: string },
): {
  email: string;
  setEmail: (value: string) => void;
  social: string;
  setSocial: (value: string) => void;
  status: 'idle' | 'loading' | 'success' | 'duplicate' | 'error';
  submit: () => Promise<void>;
  // Back to the form with the typed answers intact; a re-submit is a second
  // landing_signup_submitted, and Mailjet refreshes the same contact.
  backToForm: () => void;
} {
  const [emailValue, setEmailValue] = useState('');
  const [social, setSocial] = useState('');
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'duplicate' | 'error'
  >('idle');
  const emailStartedRef = useRef(false);

  const setEmail = (value: string) => {
    if (!emailStartedRef.current) {
      emailStartedRef.current = true;
      track('landing_email_started', { source });
    }
    setEmailValue(value);
  };

  const submit = async () => {
    if (emailValue === '') return;
    const email_domain = emailValue.split('@')[1]?.toLowerCase() ?? '';
    const social_provided = Boolean(social.trim());
    setStatus('loading');
    track('landing_signup_submitted', {
      email_domain,
      source,
      social_provided,
    });
    try {
      const res = await window.fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue, social, properties }),
      });
      if (res.ok) {
        const data: unknown = await res.json();
        if (
          typeof data === 'object' &&
          data !== null &&
          'alreadySubscribed' in data &&
          data.alreadySubscribed === true
        ) {
          setStatus('duplicate');
          track('landing_signup_duplicate', { email_domain, source });
        } else {
          setStatus('success');
          track('landing_signup_succeeded', {
            email_domain,
            source,
            social_provided,
          });
        }
      } else {
        setStatus('error');
        track('landing_signup_failed', {
          reason: 'non_ok',
          status: res.status,
          source,
        });
      }
    } catch {
      setStatus('error');
      track('landing_signup_failed', { reason: 'network_error', source });
    }
  };

  const backToForm = () => {
    setStatus('idle');
  };

  return {
    email: emailValue,
    setEmail,
    social,
    setSocial,
    status,
    submit,
    backToForm,
  };
}
